import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField } from '../components/Inputs'
import { computeDeck, DEFAULT_DECK } from '../calculators/deck'
import type { DeckInputs, DeckBoardWidth } from '../calculators/deck'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/deck/'
const CATEGORY = 'outdoor-and-landscaping'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  dimsHeading: string
  boardSizeLabel: string
  boardSizeTip: string
  boardOptions: { value: DeckBoardWidth; label: string }[]
  gapLabel: string
  gapTip: string
  includeRailing: string
  railingFtLabel: string
  railingFtTip: string
  hiddenFasteners: string
  hiddenFastenersTip: string
  wasteTip: string
  inSuffix: string
  linFtSuffix: string
  primaryName: string
  primaryLabel: string
  primaryQuery: string
  secondaryName: string
  secondaryLabel: string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: { strong: string; rest: string }[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Deck Calculator — Boards, Joists, Posts and Concrete | RenoSheets',
  desc: 'Free deck calculator. Enter deck size and board width for exact deck board pieces, joist count, post count, concrete bags and hardware. No signup.',
  h1: 'Deck Calculator',
  intro: 'Enter deck size and board width for deck board pieces, joists, posts, concrete bags and all the hardware.',
  dimsHeading: 'Deck dimensions',
  boardSizeLabel: 'Deck board size',
  boardSizeTip: 'Actual board width (not nominal). 2x6 = 5.5 in actual is the most common. 2x4 = 3.5 in for narrower boards.',
  boardOptions: [
    { value: 3.5, label: '2x4 (3.5 in actual)' },
    { value: 5.5, label: '2x6 (5.5 in actual) — most common' },
  ],
  gapLabel: 'Gap between boards',
  gapTip: 'Standard gap is 1/4 in. This allows drainage and accounts for board expansion. Composite decking often requires 3/16 in — check manufacturer spec.',
  includeRailing: 'Include railing materials',
  railingFtLabel: 'Railing linear feet',
  railingFtTip: "Total linear feet of railing runs. Don't count the house side or any openings. Typical: 2-3 sides of the deck perimeter.",
  hiddenFasteners: 'Use hidden fasteners',
  hiddenFastenersTip: 'Hidden fasteners create a clean surface with no visible screws. More expensive but popular for composite and hardwood decking.',
  wasteTip: '15% accounts for end cuts, defects and extra boards for repairs. Use 20% for decks with angles, curves or diagonal patterns.',
  inSuffix: 'in',
  linFtSuffix: 'lin ft',
  primaryName: 'Deck boards',
  primaryLabel: 'pieces at 12 ft length',
  primaryQuery: 'pressure treated 2x6 deck board 12 ft',
  secondaryName: 'Concrete bags',
  secondaryLabel: '60 lb bags for footings',
  aboutH2: 'What actually affects how much deck material you need',
  aboutP1: 'Board spacing and layout direction change material counts more than deck size alone. A 1/8 to 1/4 inch gap between boards is standard for drainage and seasonal expansion — composite decking needs less gap than wood, since it moves less with temperature. Running boards perpendicular to the joists (the usual layout) is the most efficient use of material; a diagonal pattern looks striking but increases waste by roughly a third because of the angled end cuts. Joist spacing is governed by building code, not preference: 16 inches on-center is standard for most decking, but some composite products require 12-inch spacing to prevent sagging, so always check the manufacturer\'s span rating before finalizing joist count. Footing depth follows the local frost line, not a fixed number — footings that don\'t go below frost depth can heave and shift over winter, so check with your municipality before digging.',
  howH2: 'How to calculate deck materials',
  howP1: 'Deck board count is driven by the number of rows across the deck width:',
  formula: `rows = ceil(deck width ÷ (board width + gap))
linear feet = rows × deck length × (1 + waste%)
board pieces = ceil(linear feet ÷ 12)
joists = floor(deck length ÷ 1.333) + 1  (16 in o.c.)
posts = max(4, ceil(deck length ÷ 8) × 2)`,
  howP2: 'Post spacing of 8 ft is standard for most beam/post systems. Concrete: 2 bags of 60 lb per post (10 in diameter hole, 18–24 in depth depending on frost line).',
  mistakesH3: 'Common mistakes',
  mistakes: [
    { strong: 'Skipping permits', rest: ' — most decks over 30 in from grade require a permit and inspection.' },
    { strong: 'Not accounting for crown direction', rest: ' — install boards crown-up so water runs off, not pools.' },
    { strong: 'Using indoor-rated lumber', rest: ' — always use pressure-treated, cedar or composite for exterior decking.' },
    { strong: 'Undersizing post footings', rest: ' — check your frost depth; footings must go below frost to prevent heaving.' },
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: row count × deck length with adjustable waste; joists at 16 in o.c.; posts at 8 ft centers. Last updated 2026-05-26. Estimates only — verify with your local building code before purchasing.',
  faqs: [
    { q: 'How many deck boards do I need for a 16x12 foot deck?', a: 'A 16 × 12 ft deck using 2×6 boards (5.5 in actual) with 1/4 in gaps needs about 25 rows across the 12 ft width. At 12 ft board lengths, that is 25 pieces before waste. Adding 15% waste brings the total to about 29 pieces.' },
    { q: 'How far apart should deck joists be?', a: 'Deck joists are typically spaced 16 in on-center for 5/4 or 2× decking. For 1× or thinner composite boards, 12 in spacing is recommended. A 16 ft deck needs about 13 joists at 16 in o.c. (including both rim joists).' },
    { q: 'How many concrete bags do I need per deck post?', a: 'Use 2 bags of 60 lb concrete per post hole for typical 4×4 posts in 10 in diameter holes. For larger 6×6 posts or in frost-heave areas, use 2 bags of 80 lb or 3 bags of 60 lb per post. Allow concrete to cure 24 hours before framing.' },
    { q: 'What size deck posts do I need?', a: 'For a low-to-ground deck under 6 ft, 4×4 posts on 8-ft centers are typical. For elevated decks over 6 ft, or for any beam span over 12 ft, use 6×6 posts. Always check local building codes, which often require specific post sizes based on deck load and height.' },
    { q: 'How much does it cost to build a deck per square foot?', a: 'Pressure-treated lumber decks typically cost $15–25 per sq ft for DIY material costs. Cedar decks run $25–40/sq ft. Composite decking like Trex is $35–60/sq ft. These are material costs only — contractor labor adds $20–40/sq ft depending on complexity and region.' },
    { q: 'Do I need a permit to build a deck?', a: 'In most jurisdictions, a permit is required for decks over 30 in above grade or attached to the house. Permits ensure the deck is engineered correctly for load, frost depth, and connection to the house rim joist. Always check with your local building department before starting.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de terrasse — Planches, solives, poteaux et béton | RenoSheets',
  desc: 'Calculateur de terrasse gratuit. Entrez les dimensions et la largeur des planches pour le nombre exact de planches, de solives, de poteaux, de sacs de béton et de quincaillerie. Sans inscription.',
  h1: 'Calculateur de terrasse',
  intro: 'Entrez les dimensions de la terrasse et la largeur des planches pour obtenir les planches, les solives, les poteaux, les sacs de béton et toute la quincaillerie.',
  dimsHeading: 'Dimensions de la terrasse',
  boardSizeLabel: 'Taille des planches de terrasse',
  boardSizeTip: 'Largeur réelle de la planche (pas nominale). 2x6 = 5,5 po réels est la plus courante. 2x4 = 3,5 po pour des planches plus étroites.',
  boardOptions: [
    { value: 3.5, label: '2x4 (3,5 po réels)' },
    { value: 5.5, label: '2x6 (5,5 po réels) — la plus courante' },
  ],
  gapLabel: 'Espace entre les planches',
  gapTip: 'L’espace standard est de 1/4 po. Il permet le drainage et tient compte de la dilatation des planches. Le composite exige souvent 3/16 po — vérifiez la fiche du fabricant.',
  includeRailing: 'Inclure les matériaux de garde-corps',
  railingFtLabel: 'Garde-corps en pieds linéaires',
  railingFtTip: 'Total des pieds linéaires de garde-corps. Ne comptez pas le côté de la maison ni les ouvertures. Habituellement : 2 à 3 côtés du périmètre de la terrasse.',
  hiddenFasteners: 'Utiliser des attaches dissimulées',
  hiddenFastenersTip: 'Les attaches dissimulées donnent une surface propre sans vis apparentes. Plus coûteuses, mais populaires pour le composite et le bois franc.',
  wasteTip: '15 % tient compte des coupes de bout, des défauts et de planches en plus pour les réparations. Utilisez 20 % pour les terrasses avec angles, courbes ou motifs en diagonale.',
  inSuffix: 'po',
  linFtSuffix: 'pi lin',
  primaryName: 'Planches de terrasse',
  primaryLabel: 'pièces de 12 pi de longueur',
  primaryQuery: 'pressure treated 2x6 deck board 12 ft',
  secondaryName: 'Sacs de béton',
  secondaryLabel: 'sacs de 60 lb pour les semelles',
  aboutH2: 'Ce qui influence vraiment la quantité de matériaux de terrasse',
  aboutP1: 'L’espacement des planches et le sens de la pose changent les quantités de matériaux plus que la taille de la terrasse seule. Un espace de 1/8 à 1/4 de pouce entre les planches est standard pour le drainage et l’expansion saisonnière — le bois composite a besoin de moins d’espace que le bois, car il bouge moins avec la température. Poser les planches perpendiculairement aux solives (la disposition habituelle) est l’utilisation la plus efficace du matériau; un motif en diagonale est spectaculaire mais augmente la perte d’environ un tiers à cause des coupes d’extrémité angulaires. L’espacement des solives est régi par le code du bâtiment, pas par préférence : 16 pouces centre à centre est standard pour la plupart des terrasses, mais certains produits composites exigent un espacement de 12 pouces pour éviter l’affaissement — vérifiez toujours la portée maximale du fabricant avant de finaliser le nombre de solives. La profondeur des semelles suit la ligne de gel locale, pas un chiffre fixe — des semelles qui ne descendent pas sous la profondeur de gel peuvent soulever et bouger pendant l’hiver, alors vérifiez auprès de votre municipalité avant de creuser.',
  howH2: 'Comment calculer les matériaux de terrasse',
  howP1: 'Le nombre de planches dépend du nombre de rangées sur la largeur de la terrasse :',
  formula: `rangées = arrondi.sup(largeur ÷ (largeur planche + espace))
pieds linéaires = rangées × longueur × (1 + % perte)
nombre de planches = arrondi.sup(pieds linéaires ÷ 12)
solives = arrondi.inf(longueur ÷ 1,333) + 1  (16 po c. à c.)
poteaux = max(4, arrondi.sup(longueur ÷ 8) × 2)`,
  howP2: 'Un espacement de poteaux de 8 pi est standard pour la plupart des systèmes poutre/poteau. Béton : 2 sacs de 60 lb par poteau (trou de 10 po de diamètre, 18 à 24 po de profondeur selon la ligne de gel).',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    { strong: 'Sauter les permis', rest: ' — la plupart des terrasses à plus de 30 po du sol exigent un permis et une inspection.' },
    { strong: 'Ignorer le sens du bombement', rest: ' — posez les planches bombement vers le haut pour que l’eau s’écoule sans s’accumuler.' },
    { strong: 'Utiliser du bois d’intérieur', rest: ' — utilisez toujours du bois traité, du cèdre ou du composite pour une terrasse extérieure.' },
    { strong: 'Sous-dimensionner les semelles de poteau', rest: ' — vérifiez votre profondeur de gel; les semelles doivent descendre sous le gel pour éviter le soulèvement.' },
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : nombre de rangées × longueur de la terrasse avec perte ajustable; solives à 16 po c. à c.; poteaux aux 8 pi. Dernière mise à jour 2026-05-26. Estimations seulement — vérifiez votre code du bâtiment local avant d’acheter.',
  faqs: [
    { q: 'Combien de planches de terrasse pour une terrasse de 16 x 12 pieds?', a: 'Une terrasse de 16 × 12 pi avec des planches 2×6 (5,5 po réels) et des espaces de 1/4 po demande environ 25 rangées sur la largeur de 12 pi. Avec des planches de 12 pi, cela fait 25 pièces avant la perte. En ajoutant 15 % de perte, le total atteint environ 29 pièces.' },
    { q: 'Quel espacement entre les solives de terrasse?', a: 'Les solives de terrasse sont habituellement espacées de 16 po de centre en centre pour un platelage 5/4 ou 2×. Pour des planches 1× ou du composite plus mince, un espacement de 12 po est recommandé. Une terrasse de 16 pi demande environ 13 solives à 16 po c. à c. (en comptant les deux solives de rive).' },
    { q: 'Combien de sacs de béton par poteau de terrasse?', a: 'Utilisez 2 sacs de béton de 60 lb par trou de poteau pour des poteaux 4×4 typiques dans des trous de 10 po de diamètre. Pour de plus gros poteaux 6×6 ou en zone de soulèvement par le gel, utilisez 2 sacs de 80 lb ou 3 sacs de 60 lb par poteau. Laissez le béton durcir 24 heures avant la charpente.' },
    { q: 'Quelle taille de poteaux de terrasse me faut-il?', a: 'Pour une terrasse basse de moins de 6 pi, des poteaux 4×4 aux 8 pi sont typiques. Pour des terrasses surélevées de plus de 6 pi, ou pour toute portée de poutre de plus de 12 pi, utilisez des poteaux 6×6. Vérifiez toujours le code du bâtiment local, qui exige souvent des tailles précises selon la charge et la hauteur.' },
    { q: 'Combien coûte la construction d’une terrasse au pied carré?', a: 'Les terrasses en bois traité coûtent généralement de 15 à 25 $ le pi² en matériaux pour le bricoleur. Les terrasses en cèdre se situent de 25 à 40 $/pi². Le composite comme Trex est de 35 à 60 $/pi². Ce sont des coûts de matériaux seulement — la main-d’œuvre d’un entrepreneur ajoute de 20 à 40 $/pi² selon la complexité et la région.' },
    { q: 'Ai-je besoin d’un permis pour construire une terrasse?', a: 'Dans la plupart des municipalités, un permis est exigé pour les terrasses à plus de 30 po du sol ou fixées à la maison. Les permis garantissent que la terrasse est conçue correctement pour la charge, la profondeur de gel et le raccordement à la solive de rive de la maison. Vérifiez toujours auprès de votre service du bâtiment avant de commencer.' },
  ],
}

export default function DeckCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<DeckInputs>(DEFAULT_DECK)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeDeck(i, locale), [i, locale])
  const set = (patch: Partial<DeckInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }

  const ftSuffix = locale === 'fr' ? 'pi' : 'ft'
  const sqft = locale === 'fr' ? 'pi²' : 'sq ft'

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'deck',
      name: `${c.primaryName} — ${r.deckAreaSqFt} ${sqft}`,
      createdAt: new Date().toISOString(),
      summary: `${r.deckBoardPieces} · ${r.joistCount} · ${r.postCount} · ${r.concreteBags60lb}`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'deck')

  const assumptions = locale === 'fr'
    ? `${r.deckAreaSqFt} pi² · planches de ${i.boardWidthIn} po · ${i.wastePct} % de perte · solives à 16 po c. à c.`
    : `${r.deckAreaSqFt} sq ft · ${i.boardWidthIn} in boards · ${i.wastePct}% waste · joists at 16 in o.c.`
  const summaryLine = locale === 'fr'
    ? `${r.deckAreaSqFt} pi² · ${r.deckBoardPieces} planches (12 pi chacune) · ${r.joistCount} solives · ${r.postCount} poteaux`
    : `${r.deckAreaSqFt} sq ft · ${r.deckBoardPieces} boards (12 ft each) · ${r.joistCount} joists · ${r.postCount} posts`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeDeck: row count from width ÷ (board + gap), × length, × (1 + waste%),
  // ÷ 12 ft per piece. Joists = floor(L / 1.333) + 1; posts = max(4, ceil(L/8) × 2).
  const boardPlusGapIn = Math.max(1, i.boardWidthIn) + Math.max(0, i.boardSpacingIn)
  const rows = Math.ceil(i.deckWidthFt / (boardPlusGapIn / 12))
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {locale === 'fr'
          ? `Surface: ${i.deckLengthFt} × ${i.deckWidthFt}`
          : `Deck area: ${i.deckLengthFt} × ${i.deckWidthFt}`}
        {' '}= <strong>{r.deckAreaSqFt} {sqft}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Rangées = arrondi.sup(${i.deckWidthFt} ÷ ((${i.boardWidthIn}+${i.boardSpacingIn})/12))`
          : `Rows = ceil(${i.deckWidthFt} ÷ ((${i.boardWidthIn}+${i.boardSpacingIn})/12))`}
        {' '}= <strong>{rows}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `× ${i.deckLengthFt} pi × (1 + ${i.wastePct}%)`
          : `× ${i.deckLengthFt} ft × (1 + ${i.wastePct}%)`}
        {' '}= <strong>{r.deckBoardsLinFt} {locale === 'fr' ? 'pi lin' : 'lin ft'}</strong>
      </li>
      <li>
        {locale === 'fr' ? '÷ 12 pi par planche' : '÷ 12 ft per board'}
        {' '}= <strong>{r.deckBoardPieces} {locale === 'fr' ? (r.deckBoardPieces === 1 ? 'planche' : 'planches') : (r.deckBoardPieces === 1 ? 'board' : 'boards')}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Solives: arrondi.inf(${i.deckLengthFt} ÷ 1,333) + 1`
          : `Joists: floor(${i.deckLengthFt} ÷ 1.333) + 1`}
        {' '}= <strong>{r.joistCount}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Poteaux: max(4, arrondi.sup(${i.deckLengthFt}÷8) × 2)`
          : `Posts: max(4, ceil(${i.deckLengthFt}÷8) × 2)`}
        {' '}= <strong>{r.postCount}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Béton: ${r.postCount} poteaux × 2 sacs`
          : `Concrete: ${r.postCount} posts × 2 bags`}
        {' '}= <strong>{r.concreteBags60lb} {locale === 'fr' ? 'sacs de 60 lb' : '60 lb bags'}</strong>
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('deck', 'Deck Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Outdoor & Landscaping', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('deck', 'Deck Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>

          {/* Deck size */}
          <div className="rounded-md border border-border p-3">
            <p className="mb-2 text-sm font-medium text-text-body">{c.dimsHeading}</p>
            <div className="grid grid-cols-2 gap-2">
              <SmartField label={tr('calc.length')} imperialValue={i.deckLengthFt} onChangeImperial={(n) => set({ deckLengthFt: n })} imperialUnit="ft" />
              <SmartField label={tr('calc.width')} imperialValue={i.deckWidthFt} onChangeImperial={(n) => set({ deckWidthFt: n })} imperialUnit="ft" />
            </div>
          </div>

          {/* Board width */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.boardSizeLabel}
              <InfoTip text={c.boardSizeTip} />
            </span>
            <select
              value={i.boardWidthIn}
              onChange={(e) => set({ boardWidthIn: parseFloat(e.target.value) as DeckBoardWidth })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2.5 text-sm text-text focus:border-brand focus:outline-none"
            >
              {c.boardOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Gap */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.gapLabel}
              <InfoTip text={c.gapTip} />
            </span>
            <SmartField label="" imperialValue={i.boardSpacingIn} onChangeImperial={(n) => set({ boardSpacingIn: n })} imperialUnit="in" />
          </div>

          {/* Railing */}
          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm text-text-body">
              <input type="checkbox" checked={i.needRailing} onChange={(e) => set({ needRailing: e.target.checked })} />
              {c.includeRailing}
            </label>
            {i.needRailing && (
              <div>
                <span className="mb-1 flex items-center text-sm font-medium text-text-body">
                  {c.railingFtLabel}
                  <InfoTip text={c.railingFtTip} />
                </span>
                <SmartField label="" imperialValue={i.railingLinFt} onChangeImperial={(n) => set({ railingLinFt: n })} imperialUnit="ft" />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-text-body">
              <input type="checkbox" checked={i.useHiddenFasteners} onChange={(e) => set({ useHiddenFasteners: e.target.checked })} />
              {c.hiddenFasteners}
              <InfoTip text={c.hiddenFastenersTip} />
            </label>
          </div>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={10} max={20} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">{summaryLine}</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: c.primaryLabel, qty: r.deckBoardPieces, unit: 'board', query: c.primaryQuery }}
            secondary={{ name: c.secondaryName, label: c.secondaryLabel, qty: r.concreteBags60lb, unit: 'bag', query: 'quikrete 60 lb concrete mix' }}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}><strong>{m.strong}</strong>{m.rest}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
